import { Box } from '@mui/material';
import { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useStore } from '@store/GlobalStore';
import { shallow } from 'zustand/shallow';
import { QuoteDetailPolicyAlertBanner } from '@pages/QuoteDetails/components';
import { QuotePanelsContainer } from '@pages/QuoteDetails/components';
import { QDDialogs } from '@pages/QuoteDetails/components';
import { useRef } from 'react';
import { IDialogState } from '@pages/QuoteDetails/types';
import {
  Action,
  initialDialogState,
  readOnlyQuoteStatuses,
} from '@pages/QuoteDetails/constants/QuoteDetails.constants';
import { QuoteGetResponse } from '@lio/api-types';
import { WIDTH_CONSTANTS } from '@theme/widthConstants';
import { useGetCustomer } from '@services/customer';

type FullscreenQuoteDetailsProps = {
  quote: QuoteGetResponse;
};

export const FullscreenQuoteDetails = ({ quote }: FullscreenQuoteDetailsProps) => {
  const [searchParams] = useSearchParams();
  const quoteStatus = quote?.status.id;

  const { customer } = useGetCustomer(quote?.customer?.id ?? '');

  const { isInternalUser, isUnderwriter, isLIOContractor } = useStore((state) => state.userAuth, shallow);
  const [dialogState, setDialogState] = useState<IDialogState>(initialDialogState);
  const alertBannerRef = useRef<any>(null);
  const onConfirmExit = () => {};
  const [activeRating, setActiveRating] = useState<{
    isActive: boolean;
    id: string | undefined;
  }>({ isActive: false, id: undefined });
  const handleOpenReactivateQuoteDialog = () => {
    setDialogState((prev) => ({
      ...prev,
      actionDialog: {
        show: true,
        type: Action.ReactivateQuote,
      },
    }));
  };

  const alertBannerHeight = alertBannerRef.current?.clientHeight ?? 0;
  const isReadOnlyStatus = readOnlyQuoteStatuses.includes(quoteStatus as string);
  const isReadOnly = searchParams.has('viewReadOnly');

  const statusRead = () => {
    setActiveRating({ isActive: false, id: undefined });
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100vh',
        minHeight: '600px', // Minimum height to show essential content
        width: '100%',
        paddingBottom: '120px',
        overflow: 'auto',
      }}
    >
      <QuoteDetailPolicyAlertBanner
        ref={alertBannerRef}
        quote={quote}
        customer={{
          matchState: customer?.matchState ?? undefined,
        }}
        isReadOnlyStatus={isReadOnlyStatus}
        isInternalUser={isInternalUser}
        isUnderwriter={isUnderwriter}
        isLIOContractor={isLIOContractor}
        onOpenReactivateQuoteDialog={handleOpenReactivateQuoteDialog}
      />
      <Box
        sx={{
          flex: 1,
          width: '100%',
          overflow: 'auto',
          minHeight: 'fit-content',
          '::-webkit-scrollbar': {
            display: 'none',
          },
        }}
      >
        <Box
          sx={{
            width: '100%',
            minHeight: 'fit-content',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'flex-start',
            paddingTop: `${alertBannerHeight + 20}px`,
            paddingBottom: '40px',
            paddingLeft: { xs: 2, sm: 4, md: 8 },
            paddingRight: { xs: 2, sm: 4, md: 8 },
          }}
        >
          <Box
            sx={{
              height: 'auto',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              width: {
                md: WIDTH_CONSTANTS.md,
                lg: WIDTH_CONSTANTS.lg,
                xl: WIDTH_CONSTANTS.xl,
              },
              minWidth: {
                xs: '100%',
                sm: '100%',
              },
            }}
          >
            <QuotePanelsContainer
              quote={quote}
              quoteId={quote.id}
              activeRating={activeRating}
              isReadOnly={isReadOnly}
            />
          </Box>
        </Box>
      </Box>
      <QDDialogs
        quote={quote}
        quoteId={quote.id}
        onSetDialogState={setDialogState}
        dialogState={dialogState}
        onConfirmExit={onConfirmExit}
        onStatusRead={statusRead}
      />
    </Box>
  );
};
